import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import PageLayout from "@/components/templates/PageLayout";
import useGetSkuById from "@/hooks/requests/sku/use-get-sku-by-id";
import { SIDEBAR_MESSAGES } from "@/messages/layout/sidebar";

interface SkuDetailsPageProps {
  messages: Record<string, string | Record<string, string>>; // SKU messages type
}

const SkuDetailsPage: React.FC<SkuDetailsPageProps> = ({ messages }) => {
  const { skuId, lang = "PT-BR" } = useParams<{
    productId: string;
    skuId: string;
    lang: string;
  }>();
  const navigate = useNavigate();

  const {
    data: sku,
    isLoading,
    error,
  } = useGetSkuById({
    _id: skuId ? parseInt(skuId, 10) : 0, // Fix: Não pode ser null
  });

  const pageMessages = messages[lang as keyof typeof messages] as Record<string, string>;

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <PageLayout messages={SIDEBAR_MESSAGES}>
        <PageContainer>
          <MDBox p={3}>
            <CircularProgress />
          </MDBox>
        </PageContainer>
      </PageLayout>
    );
  }

  if (error || !sku) {
    return (
      <PageLayout messages={SIDEBAR_MESSAGES}>
        <PageContainer>
          <MDBox p={3}>
            <MDTypography variant="h6" color="error">
              {pageMessages.error_loading || "Erro ao carregar SKU"}
            </MDTypography>
            <MDButton variant="contained" color="primary" onClick={handleGoBack}>
              {pageMessages.back_to_product || "Voltar ao produto"}
            </MDButton>
          </MDBox>
        </PageContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout messages={SIDEBAR_MESSAGES}>
      <PageContainer>
        <MDBox p={3}>
          <MDBox mb={3}>
            <MDTypography variant="h4" fontWeight="medium">
              {pageMessages.sku_details || "Detalhes do SKU"}
            </MDTypography>
          </MDBox>

          <MDBox>
            <MDTypography variant="h6" mb={1}>
              {pageMessages.code || "Código"}: {sku.code}
            </MDTypography>
            <MDTypography variant="body1" mb={2}>
              {pageMessages.description || "Descrição"}: {sku.title || "N/A"}
            </MDTypography>
            <MDTypography variant="body1" mb={2}>
              {pageMessages.price || "Preço"}: R${" "}
              {typeof sku.mainPrice === "number" ? sku.mainPrice.toFixed(2) : "0.00"}
            </MDTypography>
            <MDTypography variant="body1" mb={2}>
              {pageMessages.stock || "Estoque"}: {sku.stock}
            </MDTypography>
            <MDTypography variant="body1" mb={2}>
              {pageMessages.weight || "Peso"}: {sku.weight}kg
            </MDTypography>
            <MDTypography variant="body1" mb={2}>
              {pageMessages.status || "Status"}:{" "}
              {sku.active ? pageMessages.active || "Ativo" : pageMessages.inactive || "Inativo"}
            </MDTypography>
          </MDBox>

          <MDBox mt={3}>
            <MDButton variant="contained" color="primary" onClick={handleGoBack}>
              {pageMessages.back_to_product || "Voltar ao produto"}
            </MDButton>
          </MDBox>
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
};

export default SkuDetailsPage;
